import React, { useEffect, useState } from 'react';
import './DailyTimeLine.css';
import HorizontalTimeBar from './HorizontalTimeBar';
import type { MatchSlot, TournamentTablesSlots } from '@iziping/common';

interface DailyTimelineProps {
    tournamentTableSlots?: TournamentTablesSlots;
    startHour: number;
    endHour: number;
    date: Date;
    onSlotClick?: (slot: MatchSlot, rect: DOMRect) => void;
}

const DailyTimeline: React.FC<DailyTimelineProps> = ({
    tournamentTableSlots,
    startHour,
    endHour,
    date,
    onSlotClick,
}) => {
    const [currentTime, setCurrentTime] = useState(new Date());

    useEffect(() => {
        const interval = setInterval(() => setCurrentTime(new Date()), 60000); // mise à jour chaque minute
        return () => clearInterval(interval);
    }, []);

    const totalMinutes = (endHour - startHour) * 60;

    const getHourMarks = () => {
        const hours = [];
        for (let i = startHour; i <= endHour; i++) hours.push(i);
        return hours;
    };

    const calculateHourPosition = (hour: number) => {
        const minutesFromStart = (hour - startHour) * 60;
        return (minutesFromStart / totalMinutes) * 100;
    };

    const isToday = new Date().toDateString() === date.toDateString();

    const nowPosition =
        ((currentTime.getHours() * 60 + currentTime.getMinutes() - startHour * 60) / totalMinutes) * 100;

    return (
        <div className="daily-timeline">
            {/* Ligne des heures */}
            <div className="timeline-hours">
                <div className="timeline-hours-content">
                    {getHourMarks().map((hour) => (
                        <div key={hour} className="hour-mark" style={{ left: `${calculateHourPosition(hour)}%` }}>
                            {hour}h
                        </div>
                    ))}
                    {/* Ligne rouge de l'heure actuelle */}
                    {isToday && nowPosition >= 0 && nowPosition <= 100 && (
                        <div className="current-time-line" style={{ left: `${nowPosition}%` }}>
                            <span className="current-time-badge">
                                {`${currentTime.getHours()}h${String(currentTime.getMinutes()).padStart(2, '0')}`}
                            </span>
                        </div>
                    )}
                </div>
            </div>

            {/* Lignes des tables */}
            <div className="timeline-tables">
                {tournamentTableSlots?.tablesSlots?.map((table, idx) => (
                    <HorizontalTimeBar
                        key={idx}
                        label={table.table}
                        slots={table.matches}
                        startHour={startHour}
                        endHour={endHour}
                        currentTime={currentTime}
                        isEven={idx % 2 === 0}
                        referenceDate={date}
                        onSlotClick={onSlotClick}
                    />
                ))}
            </div>
        </div>
    );
};

export default DailyTimeline;
